import React, { useState, useRef } from 'react';
import { supportService } from '../../../services/supportService';
import { OrderSelector } from './OrderSelector';
import { Button, cn, fieldClassName, FieldLabel } from '../../../design-system';

interface FormField {
  name: string;
  label: string;
  type: string;
}

interface HelpTopic {
  id: number;
  title: string;
  has_form: boolean;
  form_fields: FormField[] | null;
  children: HelpTopic[] | null;
}

interface SupportFormProps {
  topics: HelpTopic[];
  actor_type: number;
  onSendMessage: (message: string) => void;
  isLastAgentMessage?: boolean;
}

export const SupportForm: React.FC<SupportFormProps> = ({
  topics,
  actor_type,
  onSendMessage,
  isLastAgentMessage = true,
}) => {
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [selectedChildTopicId, setSelectedChildTopicId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<number[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{id: number; url: string; type: 'image' | 'video'}>>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Find selected topic
  const selectedTopic = topics.find(t => t.id === selectedTopicId);
  const selectedChildTopic = selectedTopic?.children?.find(c => c.id === selectedChildTopicId);
  const finalTopic = selectedChildTopic || selectedTopic;
  const formFields = finalTopic?.form_fields || [];
  
  // Check if form needs order selection
  const needsOrderSelection = formFields.some(
    field => field.name === 'order_id' || field.name === 'item_ids'
  );
  
  // Update formData when order/item selection changes
  React.useEffect(() => {
    if (needsOrderSelection && selectedOrderId) {
      setFormData(prev => ({
        ...prev,
        order_id: selectedOrderId.toString(),
        item_ids: selectedItemIds.join(','),
      }));
    }
  }, [selectedOrderId, selectedItemIds, needsOrderSelection]);

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      const response = await supportService.uploadFile(file);
      const fileType = file.type.startsWith('video/') ? 'video' : 'image';
      setAttachments(prev => [...prev, response.file_id]);
      setUploadedFiles(prev => [...prev, {
        id: response.file_id,
        url: response.url,
        type: fileType
      }]);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      alert(error.response?.data?.detail || 'فایلت بارگذاری نشد');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (fileId: number) => {
    setAttachments(prev => prev.filter(id => id !== fileId));
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!finalTopic || submitting || isSubmitted) return;

    // Validate required fields
    if (!body.trim()) {
      alert('متن پیامت رو بنویس');
      return;
    }
    
    // Validate order selection if needed
    if (needsOrderSelection) {
      if (!selectedOrderId) {
        alert('یه سفارش رو انتخاب کن');
        return;
      }
      const itemIdsField = formFields.find(f => f.name === 'item_ids');
      if (itemIdsField && selectedItemIds.length === 0) {
        alert('حداقل یه قلم محصول رو انتخاب کن');
        return;
      }
    }

    setSubmitting(true);
    try {
      await supportService.createTicket({
        body: body.trim(),
        form_data: formData,
        actor_type: actor_type,
        help_topic_id: finalTopic.id,
        attachments: attachments,
      });

      setIsSubmitted(true);
      // Send confirmation message
      const message = `پیامت با موفقیت ثبت شد.\nموضوع: ${finalTopic.title}\nمتن: ${body.trim()}`;
      onSendMessage(message);
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      alert(error.response?.data?.detail || 'پیامت ثبت نشد');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = finalTopic && body.trim() && isLastAgentMessage && !isSubmitted;

  return (
    <div className="overflow-hidden rounded-ds-12 border border-ds-100 bg-ds-white" dir="rtl">
      {/* Header */}
      <div className="border-b border-ds-100 bg-ds-primary-soft px-4 py-3">
        <h3 className="flex items-center gap-2 text-[15px] font-bold text-ds-ink">
          <span className="text-lg">🎫</span>
          <span>فرم پشتیبانی</span>
        </h3>
        <p className="mt-1 text-[11px] text-ds-600">
          لطفاً این اطلاعات رو کامل کن
        </p>
      </div>

      {/* Form Content */}
      <div className="grid grid-cols-1 gap-4 p-4 app-sm:grid-cols-2">
        {/* Actor Type (read-only display) */}
        <div>
          <FieldLabel required>نوع کاربر</FieldLabel>
          <div className="flex h-11 items-center rounded-ds-8 bg-ds-subtle px-3 text-[13px] text-ds-700">
            {actor_type === 1 ? 'مشتری' : 'غرفه‌دار'}
          </div>
        </div>

        {/* Topic Selection */}
        <div>
          <FieldLabel required>موضوع</FieldLabel>
          <select
            value={selectedTopicId || ''}
            onChange={(e) => {
              setSelectedTopicId(Number(e.target.value));
              setSelectedChildTopicId(null);
              setFormData({});
            }}
            disabled={isSubmitted}
            className={fieldClassName}
          >
            <option value="">موضوع رو انتخاب کن</option>
            {topics.map(topic => (
              <option key={topic.id} value={topic.id}>
                {topic.title}
              </option>
            ))}
          </select>
        </div>

        {/* Child Topic Selection (if parent has children) */}
        {selectedTopic?.children && selectedTopic.children.length > 0 && (
          <div className="app-sm:col-span-2">
            <FieldLabel required>زیرموضوع</FieldLabel>
            <select
              value={selectedChildTopicId || ''}
              onChange={(e) => {
                setSelectedChildTopicId(Number(e.target.value));
                setFormData({});
              }}
              disabled={isSubmitted}
              className={fieldClassName}
            >
              <option value="">زیرموضوع رو انتخاب کن</option>
              {selectedTopic.children.map(child => (
                <option key={child.id} value={child.id}>
                  {child.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Dynamic Form Fields */}
        {formFields.length > 0 && (
          <div className="grid gap-3 app-sm:col-span-2 app-sm:grid-cols-2">
            {formFields.map(field => {
              // Skip order_id and item_ids fields - they're handled by OrderSelector
              if (field.name === 'order_id' || field.name === 'item_ids') {
                return null;
              }
              
              return (
                <div key={field.name}>
                  <FieldLabel required>{field.label}</FieldLabel>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                      disabled={isSubmitted}
                      rows={4}
                      className={cn(fieldClassName, 'min-h-24 py-3')}
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                      disabled={isSubmitted}
                      className={fieldClassName}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Order Selector (if form needs order selection) */}
        {needsOrderSelection && finalTopic && (
          <div className="app-sm:col-span-2">
            <FieldLabel required>آیتم سفارش رو انتخاب کن</FieldLabel>
            <div className="rounded-ds-8 border border-ds-200 bg-ds-canvas p-3 app-sm:p-4">
              <OrderSelector
                onSelect={(orderId, itemIds) => {
                  setSelectedOrderId(orderId);
                  setSelectedItemIds(itemIds);
                }}
                selectedOrderId={selectedOrderId}
                selectedItemIds={selectedItemIds}
              />
            </div>
          </div>
        )}

        {/* Body Text */}
        <div className="app-sm:col-span-2">
          <FieldLabel required>درباره موضوع کامل بنویس</FieldLabel>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isSubmitted}
            rows={5}
            className={cn(fieldClassName, 'min-h-28 py-3')}
            placeholder="متن پیامت رو اینجا بنویس..."
          />
        </div>

        {/* File Upload (only show if topic is selected) */}
        {finalTopic && (
          <div className="app-sm:col-span-2">
            <FieldLabel>فایل پیوست (اختیاری)</FieldLabel>
            <div className="grid grid-cols-1 gap-3 app-sm:grid-cols-2">
              {/* Image Upload */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={isSubmitted || uploading}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className={`
                    flex min-h-24 flex-col items-center justify-center rounded-ds-8 border-2 border-dashed p-4 transition-colors
                    ${isSubmitted || uploading
                      ? 'cursor-not-allowed border-ds-200 bg-ds-subtle opacity-50'
                      : 'cursor-pointer border-ds-200 bg-ds-white hover:border-ds-primary'
                    }
                  `}
                >
                  <svg className="mb-2 h-8 w-8 text-ds-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs text-ds-600">افزودن تصویر</span>
                </label>
              </div>

              {/* Video Upload */}
              <div>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  disabled={isSubmitted || uploading}
                  className="hidden"
                  id="video-upload"
                />
                <label
                  htmlFor="video-upload"
                  className={`
                    flex min-h-24 flex-col items-center justify-center rounded-ds-8 border-2 border-dashed p-4 transition-colors
                    ${isSubmitted || uploading
                      ? 'cursor-not-allowed border-ds-200 bg-ds-subtle opacity-50'
                      : 'cursor-pointer border-ds-200 bg-ds-white hover:border-ds-primary'
                    }
                  `}
                >
                  <svg className="mb-2 h-8 w-8 text-ds-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs text-ds-600">افزودن ویدئو</span>
                </label>
              </div>
            </div>
            {uploading && (
              <p className="mt-2 text-xs text-ds-500">در حال بارگذاری...</p>
            )}
            
            {/* Uploaded Files Preview */}
            {uploadedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-medium text-ds-700">
                  فایل‌های بارگذاری‌شده ({uploadedFiles.length})
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {uploadedFiles.map((file) => (
                    <div
                      key={file.id}
                      className="group relative overflow-hidden rounded-ds-8 border border-ds-100 bg-ds-subtle"
                    >
                      {file.type === 'image' ? (
                        <img
                          src={file.url}
                          alt="Uploaded"
                          className="w-full h-24 object-cover"
                        />
                      ) : (
                        <div className="w-full h-24 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      {/* Remove Button */}
                      {!isSubmitted && (
                        <button
                          onClick={() => handleRemoveFile(file.id)}
                          className="absolute top-1 left-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          title="حذف فایل"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="border-t border-ds-100 bg-ds-canvas px-4 py-3">
        {isSubmitted ? (
          <div className="flex items-center justify-center gap-2 text-xs text-ds-success">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>پیامت با موفقیت ثبت شد</span>
          </div>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            fullWidth
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>در حال ثبت...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                <span>ثبت</span>
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
